import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { COMPANY } from "../../lib/siteData";
import { LogOut, Inbox, FileText, Download, RefreshCw, Search, ChevronDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RFQ_STATUSES = ["new", "in_progress", "quoted", "closed"];
const CONTACT_STATUSES = ["new", "read", "responded"];

const AdminDashboard = () => {
  const { user, logout, headers } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("rfqs");
  const [rfqs, setRfqs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c, s] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/rfqs`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/contacts`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/stats`, { headers }),
      ]);
      setRfqs(r.data);
      setContacts(c.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        await logout();
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [headers, logout, navigate]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const updateRfqStatus = async (id, status) => {
    await axios.patch(`${BACKEND_URL}/api/admin/rfqs/${id}/status`, { status }, { headers });
    setRfqs((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
  };

  const updateContactStatus = async (id, status) => {
    await axios.patch(`${BACKEND_URL}/api/admin/contacts/${id}/status`, { status }, { headers });
    setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
  };

  const downloadAttachment = async (id, filename) => {
    const res = await axios.get(`${BACKEND_URL}/api/admin/rfqs/${id}/attachment`, {
      headers,
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || `rfq-${id}`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const filterRows = (rows, fields) =>
    rows.filter((r) => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return fields.some((f) => String(r[f] || "").toLowerCase().includes(q));
    });

  const visibleRfqs = filterRows(rfqs, ["company_name", "contact_person", "email", "product_category", "country"]);
  const visibleContacts = filterRows(contacts, ["name", "company", "email", "subject", "country"]);

  return (
    <div className="min-h-screen bg-steel-50" data-testid="admin-dashboard">
      <header className="bg-ink text-white border-b-2 border-brand">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3">
            <img src={COMPANY.logos.transparent} alt="Waves" className="h-9 w-auto brightness-0 invert opacity-90" />
            <span className="font-heading font-bold text-sm uppercase tracking-[0.2em]">Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs uppercase tracking-[0.18em] text-steel-300 hover:text-white" data-testid="admin-view-site">View site</Link>
            <span className="text-xs text-steel-400 font-mono hidden sm:inline">{user?.email}</span>
            <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] bg-brand hover:bg-brand-hover px-4 py-2 font-semibold">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-steel-200 border border-steel-200 mb-10">
          <StatCard label="RFQs · Total" value={stats?.rfqs?.total ?? "—"} icon={FileText} testId="stat-rfq-total" />
          <StatCard label="RFQs · New" value={stats?.rfqs?.new ?? "—"} icon={FileText} accent testId="stat-rfq-new" />
          <StatCard label="Contacts · Total" value={stats?.contacts?.total ?? "—"} icon={Inbox} testId="stat-contact-total" />
          <StatCard label="Contacts · New" value={stats?.contacts?.new ?? "—"} icon={Inbox} accent testId="stat-contact-new" />
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-stretch gap-px bg-steel-200 border border-steel-200">
            <button onClick={() => { setTab("rfqs"); setStatusFilter(""); }} data-testid="tab-rfqs" className={`px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold ${tab === "rfqs" ? "bg-ink text-white" : "bg-white text-ink-soft hover:bg-steel-50"}`}>
              RFQs ({rfqs.length})
            </button>
            <button onClick={() => { setTab("contacts"); setStatusFilter(""); }} data-testid="tab-contacts" className={`px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold ${tab === "contacts" ? "bg-ink text-white" : "bg-white text-ink-soft hover:bg-steel-50"}`}>
              Contacts ({contacts.length})
            </button>
          </div>
          <div className="flex items-center gap-3 flex-1 justify-end">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-steel-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" data-testid="admin-search" className="bg-white border border-steel-300 pl-9 pr-4 py-2.5 text-sm w-56 focus:border-brand focus:outline-none" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="admin-status-filter" className="bg-white border border-steel-300 px-4 py-2.5 text-sm focus:border-brand focus:outline-none">
              <option value="">All statuses</option>
              {(tab === "rfqs" ? RFQ_STATUSES : CONTACT_STATUSES).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <button onClick={fetchAll} data-testid="admin-refresh" className="inline-flex items-center gap-2 border border-steel-300 hover:border-brand px-4 py-2.5 text-sm text-ink-soft hover:text-brand">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Table */}
        {tab === "rfqs" ? (
          <RfqTable rows={visibleRfqs} onStatus={updateRfqStatus} onDownload={downloadAttachment} />
        ) : (
          <ContactTable rows={visibleContacts} onStatus={updateContactStatus} />
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, accent, testId }) => (
  <div className="bg-white p-5 lg:p-6" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className={`w-4 h-4 ${accent ? "text-brand" : "text-steel-500"}`} strokeWidth={1.5} />
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-steel-500">{label}</div>
    </div>
    <div className={`font-heading font-extrabold text-3xl ${accent ? "text-brand" : "text-ink"}`}>{value}</div>
  </div>
);

const StatusPill = ({ status }) => {
  const tone = {
    new: "bg-brand text-white",
    in_progress: "bg-amber-500 text-white",
    quoted: "bg-blue-600 text-white",
    closed: "bg-steel-300 text-ink",
    read: "bg-steel-200 text-ink-soft",
    responded: "bg-emerald-600 text-white",
  }[status] || "bg-steel-200 text-ink-soft";
  return <span className={`inline-block text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 ${tone}`}>{status}</span>;
};

const StatusDropdown = ({ value, options, onChange, testId }) => (
  <div className="relative inline-block">
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="appearance-none bg-white border border-steel-300 pl-3 pr-8 py-1.5 text-xs uppercase tracking-wider focus:border-brand focus:outline-none">
      {options.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-steel-500 pointer-events-none" />
  </div>
);

const RfqTable = ({ rows, onStatus, onDownload }) => (
  <div className="bg-white border border-steel-200 overflow-x-auto" data-testid="rfq-table">
    <table className="w-full text-sm min-w-[900px]">
      <thead className="bg-steel-50 border-b border-steel-200">
        <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-steel-500">
          <th className="px-4 py-3 font-semibold">Date</th>
          <th className="px-4 py-3 font-semibold">Company</th>
          <th className="px-4 py-3 font-semibold">Contact</th>
          <th className="px-4 py-3 font-semibold">Category</th>
          <th className="px-4 py-3 font-semibold">Country</th>
          <th className="px-4 py-3 font-semibold">Status</th>
          <th className="px-4 py-3 font-semibold">Attachment</th>
          <th className="px-4 py-3 font-semibold">Change</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={8} className="px-4 py-10 text-center text-steel-500 text-sm">No RFQs found.</td></tr>
        )}
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-steel-100 hover:bg-steel-50" data-testid={`rfq-row-${r.id}`}>
            <td className="px-4 py-3 font-mono text-xs text-steel-500">{new Date(r.created_at).toLocaleString()}</td>
            <td className="px-4 py-3 font-semibold text-ink">{r.company_name}</td>
            <td className="px-4 py-3">
              <div className="text-ink">{r.contact_person}</div>
              <a href={`mailto:${r.email}`} className="text-xs text-brand hover:underline">{r.email}</a>
              {r.phone && <div className="text-xs text-steel-500">{r.phone}</div>}
            </td>
            <td className="px-4 py-3 text-ink-soft">{r.product_category}</td>
            <td className="px-4 py-3 text-ink-soft">{r.country || "—"}</td>
            <td className="px-4 py-3"><StatusPill status={r.status} /></td>
            <td className="px-4 py-3">
              {r.attachment ? (
                <button onClick={() => onDownload(r.id, r.attachment.filename)} data-testid={`rfq-download-${r.id}`} className="inline-flex items-center gap-1.5 text-brand hover:underline text-xs font-semibold">
                  <Download className="w-3.5 h-3.5" /> {r.attachment.filename}
                </button>
              ) : <span className="text-xs text-steel-400">—</span>}
            </td>
            <td className="px-4 py-3">
              <StatusDropdown value={r.status} options={RFQ_STATUSES} onChange={(s) => onStatus(r.id, s)} testId={`rfq-status-select-${r.id}`} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {rows.length > 0 && (
      <div className="border-t border-steel-200 bg-steel-50">
        {rows.map((r) => r.description && (
          <details key={`${r.id}-desc`} className="border-b border-steel-200 last:border-0">
            <summary className="px-4 py-2.5 text-xs text-steel-600 cursor-pointer hover:bg-white">
              {r.company_name} · description
            </summary>
            <div className="px-4 py-3 text-sm text-ink-muted whitespace-pre-wrap bg-white">{r.description}</div>
          </details>
        ))}
      </div>
    )}
  </div>
);

const ContactTable = ({ rows, onStatus }) => (
  <div className="bg-white border border-steel-200 overflow-x-auto" data-testid="contact-table">
    <table className="w-full text-sm min-w-[900px]">
      <thead className="bg-steel-50 border-b border-steel-200">
        <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-steel-500">
          <th className="px-4 py-3 font-semibold">Date</th>
          <th className="px-4 py-3 font-semibold">Name</th>
          <th className="px-4 py-3 font-semibold">Company</th>
          <th className="px-4 py-3 font-semibold">Email / Phone</th>
          <th className="px-4 py-3 font-semibold">Subject</th>
          <th className="px-4 py-3 font-semibold">Country</th>
          <th className="px-4 py-3 font-semibold">Status</th>
          <th className="px-4 py-3 font-semibold">Change</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={8} className="px-4 py-10 text-center text-steel-500 text-sm">No contact submissions found.</td></tr>
        )}
        {rows.map((c) => (
          <tr key={c.id} className="border-b border-steel-100 hover:bg-steel-50" data-testid={`contact-row-${c.id}`}>
            <td className="px-4 py-3 font-mono text-xs text-steel-500">{new Date(c.created_at).toLocaleString()}</td>
            <td className="px-4 py-3 font-semibold text-ink">{c.name}</td>
            <td className="px-4 py-3 text-ink-soft">{c.company || "—"}</td>
            <td className="px-4 py-3">
              <a href={`mailto:${c.email}`} className="text-brand hover:underline text-xs">{c.email}</a>
              {c.phone && <div className="text-xs text-steel-500">{c.phone}</div>}
            </td>
            <td className="px-4 py-3 text-ink-soft">{c.subject || "—"}</td>
            <td className="px-4 py-3 text-ink-soft">{c.country || "—"}</td>
            <td className="px-4 py-3"><StatusPill status={c.status} /></td>
            <td className="px-4 py-3">
              <StatusDropdown value={c.status} options={CONTACT_STATUSES} onChange={(s) => onStatus(c.id, s)} testId={`contact-status-select-${c.id}`} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {rows.length > 0 && (
      <div className="border-t border-steel-200 bg-steel-50">
        {rows.map((c) => (
          <details key={`${c.id}-msg`} className="border-b border-steel-200 last:border-0">
            <summary className="px-4 py-2.5 text-xs text-steel-600 cursor-pointer hover:bg-white">{c.name} · message</summary>
            <div className="px-4 py-3 text-sm text-ink-muted whitespace-pre-wrap bg-white">{c.message}</div>
          </details>
        ))}
      </div>
    )}
  </div>
);

export default AdminDashboard;
